import {
  faChevronDown,
  faCheck,
  faSlidersH,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
} from 'react';
import {
  filterPopularity,
  filterPrice,
  filterPriceTravel,
  FILTER_ALL,
  FILTER_MAXPRICE,
  FILTER_MINPRICE,
  FILTER_SORTBY,
  FILTER_SUBCATEGORY,
} from '../../config/setup/setup';
import { FILTERS } from '../../config/text/text';
import { getCountryCurrency } from '../../helpers/currency';
import {
  assignSliderValueNewFilter,
  generateDynamicMarks,
  getUrlWithNewFilters,
  snapToStepMax,
} from '../../helpers/filters';
import { scrollToFilters } from '../../helpers/infiniteScroll';
import { parseWowcherPath } from '../../helpers/url';
import ThemeContext from '../../providers/ThemeProvider';
import WowModal from '../WowModal';
import FilterOptions from '../_generic/filter/FilterOptions';
import CollapsableSection from '../deal/shared-components/CollapsableSection';
import SelectedFilters from './SelectedFilters';

const FilterSubCat = React.forwardRef(
  (
    {
      count = 0,
      filterData,
      initialFilters,
      vipSearchFilter,
      inline,
      invert,
      setChosenFilters,
      priceStats,
      isSubCat = false,
    },
    ref,
  ) => {
    const theme = useContext(ThemeContext);
    const router = useRouter();
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [showSortBy, setShowSortBy] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isClearing, setIsClearing] = useState(false);
    const { searchParams, details } = parseWowcherPath(router.asPath);
    const hyperLocalArea = router?.asPath.split('/').pop();
    const staticMarks = details.isTravel ? filterPriceTravel : filterPrice;
    const currency = getCountryCurrency();
    const priceFilterKeys = useMemo(
      () => [
        FILTER_MINPRICE,
        FILTER_MAXPRICE,
        'minPrice',
        'maxPrice',
        'price',
        'priceRange',
      ],
      [],
    );

    const subCat = useMemo(() => {
      if (searchParams.has(FILTER_SUBCATEGORY)) {
        return searchParams.get(FILTER_SUBCATEGORY);
      } else if (
        details.isHyperlocal &&
        details.subCategory === hyperLocalArea
      ) {
        return FILTER_ALL;
      } else {
        return details.subCategory ?? FILTER_ALL;
      }
    }, [searchParams, details, hyperLocalArea]);

    const sortBy = useMemo(() => searchParams.get(FILTER_SORTBY) ?? '-1', [
      searchParams,
    ]);
    const currentMarks = useMemo(() => {
      const parameters = new URLSearchParams(router.asPath);
      const hasMaxPrice = parameters.has('maxPrice');
      const hasMinPrice = parameters.has('minPrice');
      if (hasMaxPrice || hasMinPrice) {
        // Only access localStorage if window is defined (client-side)
        const maxValue =
          typeof window !== 'undefined'
            ? Number(localStorage?.getItem('maxPrice')) || 1_000
            : 1_000;
        const minValue =
          typeof window !== 'undefined'
            ? Number(localStorage?.getItem('minPrice')) || 0
            : 0;

        return generateDynamicMarks(minValue, maxValue);
      } else if (!hasMaxPrice || !hasMinPrice) {
        const hasPriceStats =
          priceStats?.price &&
          ((priceStats?.price?.min !== null &&
            priceStats?.price?.min !== undefined) ||
            (priceStats?.price?.max !== null &&
              priceStats?.price?.max !== undefined));
        if (hasPriceStats) {
          const minPriceFromStats = priceStats?.price?.min || 0;
          const maxPriceFromStats = priceStats?.price?.max || 1_000;
          if (typeof window !== 'undefined') {
            localStorage.setItem('maxPrice', maxPriceFromStats.toString());
            localStorage.setItem('minPrice', minPriceFromStats.toString());
          }

          return generateDynamicMarks(minPriceFromStats, maxPriceFromStats);
        } else {
          return staticMarks;
        }
      }
    }, [priceStats, staticMarks, router.asPath]);

    const sliderValues = useMemo(() => {
      const hasPriceStats =
        priceStats?.price?.min !== null && priceStats?.price?.max !== null;
      const getPriceStatsFallback = (type) => {
        const min = Number(priceStats?.price?.min);
        const max = Number(priceStats?.price?.max);
        if (type === 'min' && typeof min === 'number') {
          return hasPriceStats ? snapToStepMax(Math.ceil(min)) : Math.ceil(min);
        }
        if (type === 'max' && typeof max === 'number') {
          return hasPriceStats ? snapToStepMax(Math.ceil(max)) : Math.ceil(max);
        }

        return null;
      };

      const minValue =
        Number(searchParams?.get(FILTER_MINPRICE)) ??
        getPriceStatsFallback('min') ??
        0;

      const maxValue =
        Number(searchParams?.get(FILTER_MAXPRICE)) ??
        getPriceStatsFallback('max') ??
        null;

      return [
        assignSliderValueNewFilter(Number(minValue), currentMarks, 0),
        assignSliderValueNewFilter(
          Number(maxValue),
          currentMarks,
          currentMarks.length - 1,
        ),
      ];
    }, [searchParams, currentMarks, priceStats]);

    const sortSearchParameters = (searchUrl) => {
      const parameters = new URLSearchParams(searchUrl);
      const sortedEntries = Array.from(parameters.entries()).sort(([a], [b]) =>
        a.localeCompare(b),
      );

      return new URLSearchParams(sortedEntries).toString();
    };

    const validateFilters = useCallback((filters) => {
      if (!filters || typeof filters !== 'object') return {};

      const validatedFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }

        if (!Array.isArray(value)) {
          if (typeof value === 'number') {
            console.warn(`Rejecting numeric filter value for ${key}:`, value);

            return;
          }
          validatedFilters[key] = [value];
        } else {
          const filteredArray = value.filter((v) => {
            if (typeof v === 'number') {
              console.warn(
                `Rejecting numeric value in filter array for ${key}:`,
                v,
              );

              return false;
            }

            return v !== undefined && v !== null && v !== '';
          });

          if (filteredArray.length > 0) {
            validatedFilters[key] = filteredArray;
          }
        }
      });

      return validatedFilters;
    }, []);

    const setSelectedFiltersSafe = useCallback(
      (filtersOrUpdater) => {
        if (typeof filtersOrUpdater === 'function') {
          setSelectedFilters((previous) => {
            const newFilters = filtersOrUpdater(previous);

            return validateFilters(newFilters);
          });
        } else {
          setSelectedFilters(validateFilters(filtersOrUpdater));
        }
      },
      [validateFilters],
    );

    const updateFilter = useCallback(
      (filterParameters, preserveExistingFilters = true, newVipFilter) => {
        const finalVipFilter = newVipFilter ?? vipSearchFilter;
        const finalFilterParameters = { ...(filterParameters || {}) };

        // Merge with existing filters if needed
        if (preserveExistingFilters) {
          Object.entries(selectedFilters).forEach(([key, values]) => {
            const safeValues = Array.isArray(values) ? values : [values];
            if (
              safeValues.length > 0 &&
              safeValues.some((v) => v) &&
              !Object.prototype.hasOwnProperty.call(finalFilterParameters, key)
            ) {
              finalFilterParameters[key] = safeValues.filter(Boolean).join(',');
            }
          });
        }

        // Clean null values
        Object.keys(finalFilterParameters).forEach((key) => {
          if (finalFilterParameters[key] === null) {
            delete finalFilterParameters[key];
          }
        });

        const url = getUrlWithNewFilters(
          finalFilterParameters,
          finalVipFilter,
          currentMarks,
          isSubCat,
        );

        try {
          const newSearch = new URL(url, window.location.origin).search;

          // Only update if URL has actually changed
          if (
            sortSearchParameters(window.location.search) !==
            sortSearchParameters(newSearch)
          ) {
            router.replace(url, url, { shallow: true });
            if (!isSubCat) {
              scrollToFilters(router, 0);
            }
          }
        } catch (error) {
          console.error('Invalid URL in updateFilter', error);
        }
      },
      [router, currentMarks, selectedFilters, vipSearchFilter],
    );

    useEffect(() => {
      if (initialFilters && Object.keys(initialFilters).length > 0) {
        setSelectedFiltersSafe(initialFilters);
      }
    }, [initialFilters, setSelectedFiltersSafe]);

    useEffect(() => {
      if (isInitialLoad || isClearing) {
        if (isInitialLoad) setIsInitialLoad(false);

        return;
      }

      const filterParameters = {
        sliderValues,
        sortBy,
        subCat,
      };

      Object.entries(selectedFilters).forEach(([key, values]) => {
        if (
          Array.isArray(values) &&
          values.length > 0 &&
          values.some((v) => v.trim())
        ) {
          filterParameters[key] = values
            .filter((v) => v.trim())
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(',');
        }
      });

      updateFilter(filterParameters, false);
    }, [
      selectedFilters,
      isClearing,
      isInitialLoad,
      sliderValues,
      sortBy,
      subCat,
      updateFilter,
    ]);

    const handleClearAllFilters = useCallback(() => {
      setIsClearing(true);
      setSelectedFiltersSafe({});
      if (showFilters) {
        setShowFilters(false);
      }
      const filterParameters = {
        [FILTER_MAXPRICE]: null,
        [FILTER_MINPRICE]: null,
        [FILTER_SORTBY]: null,
      };

      Object.keys(filterData).forEach((filterKey) => {
        filterParameters[filterKey] = null;
      });
      const url = getUrlWithNewFilters(
        filterParameters,
        vipSearchFilter,
        currentMarks,
      );
      router.replace(url, url, { shallow: true });

      setTimeout(() => {
        setIsClearing(false);
      }, 200);
    }, [
      setSelectedFiltersSafe,
      filterData,
      currentMarks,
      router,
      vipSearchFilter,
    ]);

    const getFilterLabel = useCallback(
      (category, id) => {
        const categoryData = filterData[category];
        const item = categoryData?.find((item) => item.id === id);

        return item ? item.linkText : id;
      },
      [filterData],
    );

    const removeFilter = useCallback(
      (category, value) => {
        setSelectedFiltersSafe((previous) => {
          if (!previous[category] || !Array.isArray(previous[category])) {
            return previous;
          }

          const updatedFilters = {
            ...previous,
            [category]: previous[category].filter((item) => item !== value),
          };

          if (updatedFilters[category].length === 0) {
            delete updatedFilters[category];
          }

          return updatedFilters;
        });
      },
      [setSelectedFiltersSafe],
    );

    const getActiveFilters = useMemo(() => {
      const activeFilters = [];

      Object.entries(selectedFilters).forEach(([category, values]) => {
        if (priceFilterKeys.includes(category)) {
          return;
        }

        if (Array.isArray(values)) {
          values.forEach((value) => {
            if (value !== undefined && value !== null && value !== '') {
              activeFilters.push({
                category,
                label: getFilterLabel(category, value),
                value,
              });
            }
          });
        }
      });
      // Handle price filter chips
      const [minSliderValue, maxSliderValue] = sliderValues;
      const minMarkValue = currentMarks[0]?.value || 0;
      const maxMarkValue =
        currentMarks[currentMarks.length - 1]?.value || 1_000;
      //  price values have been modified from  defaults
      const isPriceFilterActive =
        minSliderValue !== 0 || maxSliderValue !== currentMarks.length - 1;

      if (isPriceFilterActive) {
        const minPrice = currentMarks[minSliderValue]?.value || minMarkValue;
        const maxPrice = currentMarks[maxSliderValue]?.value || maxMarkValue;

        //  price range chip
        if (minPrice !== minMarkValue || maxPrice !== maxMarkValue) {
          activeFilters.push({
            category: 'price',
            isPriceFilter: true,
            label: `${currency}${minPrice} - ${currency}${maxPrice}`,
            maxPrice,
            minPrice,
            value: 'price_range',
          });
        }
      }

      if (sortBy && sortBy !== '-1') {
        const sortOption = filterPopularity.find(
          (option) => option.value === sortBy,
        );
        if (sortOption) {
          activeFilters.push({
            category: 'sort',
            isSortFilter: true,
            label: `${sortOption.name}`,
            value: 'sort_by',
          });
        }
      }
      setChosenFilters(activeFilters);

      return activeFilters;
    }, [
      selectedFilters,
      sortBy,
      getFilterLabel,
      priceFilterKeys,
      sliderValues,
      currentMarks,
    ]);

    const handleRemoveFilter = useCallback(
      (category, value) => {
        if (category === 'sort' && value === 'sort_by') {
          const filterParameters = {
            [FILTER_SORTBY]: null,
            sliderValues,
            sortBy: null,
            subCat,
          };
          updateFilter(filterParameters, true);
        } else if (category === 'price' && value === 'price_range') {
          const defaultMinIndex = 0;
          const defaultMaxIndex = currentMarks.length - 1;

          const filterParameters = {
            [FILTER_MAXPRICE]: null,
            [FILTER_MINPRICE]: null,
            sliderValues: [defaultMinIndex, defaultMaxIndex],
            sortBy,
            subCat,
          };
          updateFilter(filterParameters, true);
        } else {
          removeFilter(category, value);
        }
      },
      [sliderValues, subCat, updateFilter, removeFilter, sortBy, currentMarks],
    );

    const handleSortSelection = useCallback(
      (optionValue) => {
        setShowSortBy(false);

        if (sortBy !== optionValue) {
          const filterParameters = {
            sliderValues,
            sortBy: optionValue,
            subCat,
          };
          updateFilter(filterParameters, true);
        }
      },
      [sliderValues, subCat, updateFilter, sortBy],
    );

    useImperativeHandle(ref, () => ({
      handleClearAllFilters,
      handleRemoveFilter,
      handleSortSelection,
    }));

    return (
      <div className="filter-container">
        {/* Mobile View - Buttons */}
        <div className="mobile-view">
          <div className="filter-buttons">
            <div className="sort-btn-wrapper">
              <button
                className="sort-btn"
                onClick={() => setShowSortBy(!showSortBy)}
                type="button"
              >
                <span>Sort By</span>
                <FontAwesomeIcon
                  icon={showSortBy ? faChevronUp : faChevronDown}
                />
              </button>
              {showSortBy && (
                <div className="sort-dropdown-container">
                  <div className="sort-dropdown-content">
                    <div className="sort-options">
                      {filterPopularity.map((option) => (
                        <button
                          className={`sort-option ${
                            sortBy === option.value ? 'selected' : ''
                          }`}
                          key={option.value}
                          onClick={() => handleSortSelection(option.value)}
                          type="button"
                        >
                          <span>{option.name}</span>
                          {sortBy === option.value && (
                            <FontAwesomeIcon icon={faCheck} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sort-btn-wrapper">
              <button
                className="filters-btn"
                onClick={() => setShowFilters(true)}
                type="button"
              >
                <FontAwesomeIcon
                  icon={faSlidersH}
                  style={{ fontSize: '18px' }}
                />
                <span>Filters</span>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop View - Full Content */}
        <div className="desktop-view">
          <div className="filters-content">
            <CollapsableSection
              className="isSubcat-section"
              collapsed={collapsed}
              isSubCat
              onToggle={() => setCollapsed((state) => !state)}
              title={'Sort By'}
            >
              <div className="sort-section">
                <div className="sort-options">
                  {filterPopularity.map((option) => (
                    <button
                      className={`sort-option ${
                        sortBy === option.value ? 'selected' : ''
                      }`}
                      key={option.value}
                      onClick={() => handleSortSelection(option.value)}
                      type="button"
                      value={option?.value}
                    >
                      <span>{option.name}</span>
                      {sortBy === option.value && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </CollapsableSection>

            <div className="filter-section">
              <FilterOptions
                categories={subCat}
                count={count}
                currentMarks={currentMarks}
                filterData={filterData}
                inline={inline}
                invert={invert}
                isSubCat={isSubCat}
                priceStats={priceStats}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFiltersSafe}
                sliderValues={sliderValues}
                sortBy={sortBy}
                updateFilter={(parameters, preserve, vipFilter) =>
                  updateFilter(parameters, preserve, vipFilter)
                }
                vipSearchFilter={vipSearchFilter}
              />
            </div>
          </div>
        </div>

        <div className="mobile-view">
          <SelectedFilters
            activeFilters={getActiveFilters}
            onClearAll={handleClearAllFilters}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>
        {/* Mobile Filter Modal */}
        <WowModal
          ariaLabelledBy="filters-modal"
          centered
          extraClasses="search-filter with-fade-in no-body-padding"
          searchFilters
          setShowModal={setShowFilters}
          showModal={showFilters}
          title={FILTERS}
        >
          <div className="filters-modal-wrapper">
            <div className="modal-content-wrapper">
              <FilterOptions
                categories={subCat}
                currentMarks={currentMarks}
                filterData={filterData}
                inline={inline || false}
                invert={invert || false}
                isSubCat={isSubCat}
                priceStats={priceStats}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFiltersSafe}
                sliderValues={sliderValues}
                sortBy={sortBy}
                updateFilter={(parameters, preserve, vipFilter) =>
                  updateFilter(parameters, preserve, vipFilter)
                }
                vipSearchFilter={vipSearchFilter}
              />
            </div>
            <div className="modal-footer">
              <span className="clear-btn" onClick={handleClearAllFilters}>
                Clear All
              </span>
              <button
                className="apply-btn"
                onClick={() => setShowFilters(false)}
              >{`View ${count} ${count > 1 ? 'Results' : 'Result'}`}</button>
            </div>
          </div>
        </WowModal>

        <style jsx>{`
          .filter-container {
            width: 100%;
            margin: 0px auto;
            position: relative;
            margin-bottom: 10px;
          }

          /* Mobile View - Buttons & Modal */
          .mobile-view {
            display: block;
            width: 100%;
          }
          .filter-buttons {
            display: flex;
            gap: 8px;
            margin-bottom: 12px;
            width: 100%;
          }
          .sort-btn-wrapper {
            flex: 1;
            position: relative;
          }
          .sort-btn,
          .filters-btn {
            flex: 1;
            position: relative;
            width: 100%;
            background: ${theme.colors.searchbg};
            color: ${theme.colors.primary};
            border: 1px solid ${theme.colors.primary};
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: all 0.2s ease;
          }
          .sort-btn {
            justify-content: space-between;
          }
          .filters-btn {
            justify-content: flex-start;
            gap: 8px;
          }
          .sort-dropdown-container {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 1000;
            margin-top: 4px;
          }
          .filter-count {
            background: white;
            color: ${theme.colors.primary};
            border-radius: 50%;
            width: 22px;
            height: 22px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 700;
            margin-left: 8px;
          }
          .modal-content-wrapper {
            overflow-y: auto;
          }
          .sort-dropdown-container {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 1000;
            margin-top: 4px;
          }

          .sort-dropdown-content {
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            border: 1px solid #e0e0e0;
            overflow: hidden;
          }

          .sort-option {
            width: 100%;
            background: none;
            border: none;
            padding: 6px 10px;
            text-align: left;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background-color 0.2s;
            font-size: 14px;
          }

          .sort-option:hover {
            color: ${theme.colors.primary};
          }

          .sort-option.selected {
            color: ${theme.colors.primary};
          }
          .desktop-view {
            scrollbar-color: #ccc #ffffff;
            scrollbar-width: thin;
          }

          /* Desktop View - Full Content */
          .desktop-view {
            display: none;
            width: 100%;
            border: 1px solid ${theme.colors.primary};
            border-radius: 10px;
            background: white;
            max-height: 85vh;
            overflow-y: auto;
            scrollbar-width: thin;
          }

          .filters-content {
            width: 100%;
            max-height: 100%;
            overflow-y: auto;
            overflow-x: hidden;
            padding-right: 1px;
            scrollbar-width: thin;
          }

          .sort-section {
            width: 100%;
          }

          .filter-section {
            width: 100%;
          }

          .section-title {
            margin-bottom: 12px;
            font-size: 16px;
            font-weight: 600;
            color: ${theme.colors.primary};
          }

          /* Modal Styles */
          .filters-modal-wrapper {
            background: white;
            width: 100%;
            max-width: 500px;
            height: max-content;
            max-height: max-content;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            margin-top: -5px;
          }
          .fullscreen-mobile .modal-dialog {
            width: 100%;
            min-width: 100%;
            min-height: 100%;
            height: 100%;
            top: 0;
            max-width: 100%;
            max-height: 100%;
            position: absolute;
            margin: 0;
          }
          .modal-footer {
            display: flex;
            padding: 10px 10px;
            border-top: 1px solid #e0e0e0;
            background: white;
            position: sticky;
            margin-bottom: 10px;
            align-items: center;
            justify-content: center;
          }

          .clear-btn {
            flex: 1;
            color: ${theme.colors.primary};
            padding: 10px 0px;
            border-radius: 25px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            text-decoration: underline;
            background: transparent;
            transition: all 0.2s ease;
            min-height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .apply-btn {
            flex: 1;
            background: ${theme.colors.primary};
            color: white;
            border: none;
            padding: 0px 5px;
            border-radius: 20px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            min-height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 30px;
          }
          .apply-btn:hover {
            opacity: 0.9;
          }

          /* Responsive Breakpoints */
          @media (max-width: ${theme.breakpoints.lgDown}) {
            /* Show mobile view for all devices up to large desktop */
            .filter-container {
              margin: 0;
            }
            .mobile-view {
              display: block;
            }
            .desktop-view {
              display: none;
            }

            @media (min-width: ${theme.breakpoints.smUp}) {
              .filter-buttons {
                gap: 8px;
              }
              .sort-btn,
              .filters-btn {
                padding: 14px 20px;
                font-size: 17px;
              }
              .filter-count {
                width: 24px;
                height: 24px;
                font-size: 13px;
              }
              .filters-modal-wrapper {
                height: 80vh;
                max-width: unset;
              }
              .sort-option {
                padding: 14px 20px;
                font-size: 16px;
              }
            }

            /* Small mobile adjustments */
            @media (max-width: ${theme.breakpoints.smDown}) {
              .filter-container {
                margin: 0;
              }
              .filter-buttons {
                flex-direction: row;
                gap: 8px;
                padding: 0 10px;
                margin-top: 10px;
              }

              .sort-btn,
              .filters-btn {
                width: 100%;
              }
            }
          }

          @media (min-width: ${theme.breakpoints.lgUp}) {
            /* Desktop view only for large screens */
            .mobile-view {
              display: none;
            }
            .desktop-view {
              display: flex;
            }
          }

          /* Dark mode adjustments for VIP search */
          ${vipSearchFilter &&
          `
          .desktop-view {
            background: rgba(0,0,0,0.2);
            border-color: rgba(255,255,255,0.1);
          }
        `}
        `}</style>
      </div>
    );
  },
);

FilterSubCat.propTypes = {
  count: PropTypes.number,
  filterData: PropTypes.object.isRequired,
  initialFilters: PropTypes.object,
  inline: PropTypes.bool,
  invert: PropTypes.bool,
  priceStats: PropTypes.object,
  setChosenFilters: PropTypes.func,
  vipSearchFilter: PropTypes.bool,
};

FilterSubCat.defaultProps = {
  count: 0,
  initialFilters: {},
  inline: false,
  invert: false,
  priceStats: {},
  setChosenFilters: () => {},
  vipSearchFilter: false,
};

export default FilterSubCat;
